import { Badge } from '@deepguide-ai/ui/badge'
import type { EvidenceScoreResult } from '@deepguide-ai/types'
import { cn } from '@/lib/utils'

interface QualityScoreDisplayProps {
  score: number
  result: EvidenceScoreResult
  qualityTier?: string | null
  /** Compact mode uses smaller text */
  compact?: boolean
  /** Show as inline (no wrapper) vs card style */
  variant?: 'inline' | 'card'
  className?: string
}

const getScoreColor = (score: number) => {
  if (score >= 90) return 'text-status-green'
  if (score >= 70) return 'text-yellow-600'
  if (score >= 50) return 'text-orange-600'
  return 'text-status-red'
}

const getQualityTierClass = (tier: string) => {
  switch (tier) {
    case 'EXCELLENT':
      return 'border-green-300 text-green-700 dark:border-green-600 dark:text-green-400'
    case 'ACCEPTABLE':
      return 'border-blue-300 text-blue-700 dark:border-blue-600 dark:text-blue-400'
    case 'NEEDS_IMPROVEMENT':
      return 'border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-400'
    default:
      return 'border-red-300 text-red-700 dark:border-red-600 dark:text-red-400'
  }
}

const tierBadgeCompactClass =
  '!inline-flex !h-5 !min-h-5 !items-center !justify-center !rounded-md !px-1.5 !py-0 !text-[9px] !font-semibold !leading-none uppercase tracking-[0.06em] !shadow-none'

const getQualityTierBadge = (tier: string | null | undefined) => {
  switch (tier) {
    case 'EXCELLENT':
      return (
        <Badge
          className={cn(
            tierBadgeCompactClass,
            'border-status-green/30 bg-status-green-bg text-status-green-strong'
          )}
        >
          Excellent
        </Badge>
      )
    case 'ACCEPTABLE':
      return (
        <Badge
          className={cn(
            tierBadgeCompactClass,
            'border-status-blue/30 bg-status-blue-bg text-status-blue-strong'
          )}
        >
          Acceptable
        </Badge>
      )
    case 'NEEDS_IMPROVEMENT':
      return (
        <Badge
          className={cn(
            tierBadgeCompactClass,
            'border-status-orange/30 bg-status-orange-bg text-status-orange-strong'
          )}
        >
          Needs Improvement
        </Badge>
      )
    case 'FAIL':
      return (
        <Badge
          className={cn(
            tierBadgeCompactClass,
            'border-status-red/30 bg-status-red-bg text-status-red-strong'
          )}
        >
          Fail
        </Badge>
      )
    default:
      return null
  }
}

/**
 * Shared component for displaying quality scores with consistent styling.
 * Handles the "no evidence" state with appropriate textual messaging.
 */
export function QualityScoreDisplay({
  score,
  result,
  qualityTier,
  compact = false,
  variant = 'inline',
  className,
}: QualityScoreDisplayProps) {
  const resultMessage = {
    NO_EVIDENCE: 'No substantive evidence detected',
    PARTIAL: 'Partial evidence',
    INSUFFICIENT: 'Evidence does not satisfy the requirements',
    SUFFICIENT: null,
  }[result]

  if (variant === 'card') {
    const scoreValue = (
      <div className='flex items-center gap-1 leading-none'>
        {resultMessage ? (
          <>
            <span
              className={cn(
                'font-medium text-text-secondary',
                compact ? 'text-[13px]' : 'text-[15px]'
              )}
            >
              {resultMessage}
            </span>
            <span className='text-[13px] text-text-subtle'>({Math.round(score)}/100)</span>
          </>
        ) : (
          <>
            <span
              className={cn(
                'tabular-nums',
                compact
                  ? 'text-[13px] font-semibold'
                  : 'text-[22px] font-bold',
                getScoreColor(score)
              )}
            >
              {Math.round(score)}
            </span>
            <span
              className={cn(
                'text-text-subtle',
                compact ? 'text-[12px]' : 'pb-px text-[13px]'
              )}
            >
              /100
            </span>
          </>
        )}
      </div>
    )

    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        {scoreValue}
        {qualityTier ? getQualityTierBadge(qualityTier) : null}
      </div>
    )
  }

  // Inline variant - used in SubmissionDetailsCard
  if (resultMessage) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <span className={cn(
          'font-medium text-muted-foreground',
          compact ? 'text-base' : 'text-lg'
        )}>
          {resultMessage}
        </span>
        <span className='text-sm text-muted-foreground/60'>({Math.round(score)}/100)</span>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className={cn(
        'font-bold',
        compact ? 'text-xl' : 'text-2xl',
        getScoreColor(score)
      )}>
        {Math.round(score)}/100
      </span>
      {qualityTier && (
        <Badge variant='outline' className={getQualityTierClass(qualityTier)}>
          {qualityTier.replace('_', ' ')}
        </Badge>
      )}
    </div>
  )
}

// Export helpers for use elsewhere
export { getScoreColor, getQualityTierClass, getQualityTierBadge }
